import { Component, OnInit, ViewEncapsulation, ViewChild, Input, Output, EventEmitter, ElementRef, ViewContainerRef, OnChanges, OnDestroy } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription, switchMap, tap, of, Observable, catchError, timeout } from 'rxjs';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { DQOIService } from '@app/cp/shared/services/DQOI.service';
import { AutoCompleteComponent, ComboBoxComponent, PopupSettings } from '@progress/kendo-angular-dropdowns';
import { DQOISearchParam } from '@app/cp/shared/models/DQOISearchParam';
import { SearchTarget } from '@app/cp/shared/enums/SearchTarget';
import { ControlType } from '@app/cp/shared/enums/ControlTypes';
import { DQOISearchEvent } from '../../models/DQOISearchEvent';
import { CommonFunctions } from '../../static/common-functions';
import { v_QuickSearchBus } from '@app/cp/shared/models/v_QuickSearchBus';
import { AppClaims } from '../../models/AppClaims';
import { CPSearchInputParam } from '../../models/CPSearchInputParam';
import { CommonParam } from '../../models/CommonParam';
import { FeatureFlagService } from '@app/cp/shared/services/feature-flag.service';
import { CmProAuthService } from '@app/core/services';
import { UsersBus } from '../../models/UsersBus.model';
import { popoutService } from '../../services/popout.service';

type FilterChangeResult = { data: any, filter: string, skipProcessing: boolean };

type FilterChangeEvent = { searchTarget: any, filter: string; };

@Component({
    selector: 'dqoi-quick-search',
    templateUrl: './dqoi-quick-search.component.html',
    styleUrls: ['./dqoi-quick-search.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DQOIQuickSearchComponent implements OnInit, OnChanges, OnDestroy {
    SearchTarget: SearchTarget;
    ControlType = ControlType;
    controlTypes = ControlType;
    quickSearchAnchor: ViewContainerRef;
    public popupSettings: PopupSettings;

    advSearchInputParam: CPSearchInputParam = new CPSearchInputParam();

    @ViewChild("searchAC") ac: AutoCompleteComponent;
    @ViewChild("searchCB") cb: ComboBoxComponent;
    @Input() minSearchSize: number = 3;
    @Input() showFound: boolean = true;
    @Input() searchTarget: SearchTarget = SearchTarget.dispatch;
    @Input() controlType: ControlType = ControlType.comboBox;
    @Output() selected: EventEmitter<any> = new EventEmitter<any>();
    customerId: any;
    searchDataValue: v_QuickSearchBus;

    readOnly: boolean = false;
    isActive: boolean = true;
    loading: boolean = false;
    public searchText: string;
    rights: AppClaims = new AppClaims();
    subscriptions: Subscription[] = [];
    public searchFilter: string;
    event: DQOISearchEvent = new DQOISearchEvent();
    featureFlagSet: any = {};
    private filterSubject: Subject<FilterChangeEvent> = new Subject();
    searchButtonTitle: string = "Only Search Dispatches";
    constructor(private globals: GlobalsService, private svc_data: DQOIService,
        protected featureFlagService: FeatureFlagService, private authService: CmProAuthService, public popout: popoutService) {

        this.subscriptions.push(svc_data.quickSearchAnchor$.subscribe(anchor => {
            this.quickSearchAnchor = anchor;
            this.setPopupSettings();
        }));

        this.featureFlagService.featureFlagSets.subscribe(data => {
            this.featureFlagSet = data;
            this.searchButtonTitle = "Only Search " + this.getDispatchTitleName();
        });
    }
    data: v_QuickSearchBus[];
    searchParam: DQOISearchParam = new DQOISearchParam();
    ngOnInit() {
        this.searchParam.minStringSize = this.minSearchSize;
        this.subscriptions.push(this.filterSubject.pipe(
            debounceTime(600),
            distinctUntilChanged(),
            tap((value) => {
                this.setLoading(true);
                this.data = [];
            }),
            switchMap((filterEvt: FilterChangeEvent) => {
                return this.filterChangeSubject(filterEvt.filter).pipe(
                    timeout(120000), //2 min timeout
                    catchError((error) => {
                        console.error('Error during search:', error);
                        if (error.name === 'TimeoutError') {
                            this.globals.notifyError("Unable to complete search. Search timed out. Please try search again", true);
                        }
                        return of({
                            filter: filterEvt.filter,
                            data: [],
                            skipProcessing: false
                        });
                    })
                );
            })
        ).subscribe({
            next: (result: FilterChangeResult) => {
                if (!result.data.success && result.data?.message) {
                    console.error(result.data?.message);
                }
                if (result.skipProcessing || result.filter != this.savedSearchValue) {
                    this.setLoading(false);
                    return;
                }

                this.data = result.data;
                this.processSearchResults(result.data);

                if (result.filter.length > 2) {
                    this.searchFilter = result.filter.replace(/\*/g, "");
                }
                this.setLoading(false);
            },
            error: e => {
                console.error('Error during search:', e);
                this.setLoading(false);
            },
            complete: () => {
                this.setLoading(false);
            }
        }));

        this.authService.subscribeToLoginEvents((user: UsersBus) => {
            if (user) {
                this.rights = user.appClaims;
                this.isActive = (this.rights.SearchDispatchs != "All");
            }
        });
        this.changeSearchText();
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => { subscription.unsubscribe(); });
        this.authService.unsubscribeToLoginEvents();
    }

    public opened: boolean;
    @ViewChild("search") searchField: ElementRef;
    searchWindow() {
        this.opened = !this.opened;
    }
    searchFocus(): void {
        setTimeout(() => { // this will make the execution after the above boolean has changed
            this.searchField.nativeElement.focus();
        }, 0);
    }

    showPreviousResults(event: any) {
        this.setLoading(true);
        setTimeout(() => {
            if (this.data && this.data.length > 0 && !this.ac.isOpen) {
                const tempdata = this.data;
                this.data = [];
                this.ac.toggle(true);
                setTimeout(() => {
                    this.data = tempdata;
                    this.setLoading(false);
                }, 10);
            }
        }, 0);
    }

    get showPreviousSearchResults(): boolean {
        return this.data && this.data.length > 0 &&
            this.savedSearchValue === this.ac.value &&
            !this.ac.isOpen;
    }

    get showRerunQueryIcon(): boolean {
        return this.ac && this.ac.value && !this.ac.isOpen;
    }
    closeSearchResultsEvent() {
        this.ac.toggle(false);
    }

    onClose(event: any): void {
        event.preventDefault();
        setTimeout(() => {
            if ((this.data && this.data.length > 0) || this.loading) {
                if (!this.ac.isOpen) {
                    this.ac.toggle(true);
                }
            } else {
                this.ac.toggle(false);
            }
        }, 0);
    }

    setPopupSettings() {
        this.popupSettings = {
            popupClass: 'cpdiSearch',
            appendTo: this.quickSearchAnchor,
        };
    }
    public cellClick({ dataItem }): void {
        let id = dataItem.id;
        let typeId = "1";
        if (id == null) {
            if (dataItem.dispatchId != null)
                id = dataItem.dispatchId;
            else if (dataItem.invoiceId)
                id = dataItem.invoiceId;
            else if (dataItem.quoteId) {
                typeId = "2";
                id = dataItem.quoteId;
            }
            else if (dataItem.orderId) {
                typeId = "3";
                id = dataItem.orderId;
            }
        }
        if (this.globals.user.isZuggitUser && dataItem.domainAvailability?.toLowerCase() === 'climatepros') {
            this.openPopoutLink(`/popout/service/${typeId}/${id}`);
        } else {
            this.valueChange(id, dataItem.isConstruction);
        }
    }

    onFiltering(event: any) {
        this.data = [];
    }
    rerunQuery() {
        const evt = {
            filter: this.searchFilter,
            searchTarget: this.searchTarget
        } as FilterChangeEvent;
        this.data = [];
        this.ac.toggle(true);
        this.filterSubject.next(evt);
    }
    filterChange(filter: string) {
        const evt = {
            filter: filter,
            searchTarget: this.searchTarget
        } as FilterChangeEvent;
        this.ac.value = this.savedSearchValue = filter;
        this.filterSubject.next(evt);
    }
    filterChangeSubject(filter: string): Observable<FilterChangeResult> {
        if (!CommonFunctions.hasStringValue(filter) || filter.length < this.minSearchSize) {
            return of({
                filter,
                data: [],
                skipProcessing: true
            });
        }

        this.searchParam.userId = this.globals.userId;
        this.searchParam.search = filter.trim();
        this.searchParam.searchTarget = this.searchTarget;
        //NOTE: [dturco 12.30.2024] This is NOT used by dispatch search. As of 12.25.2024 Active dispatch searching goes back 24 months, ALL looks all att records in DB
        this.searchParam.startDate = new Date(2013, 1, 1);
        this.searchParam.isOpen = this.isActive;

        const searchFunctions = {
            1: this.svc_data.dispatch_search.bind(this.svc_data),
            2: this.svc_data.quote_search.bind(this.svc_data),
            3: this.svc_data.order_search.bind(this.svc_data),
            4: this.svc_data.invoice_search.bind(this.svc_data),
        };

        const searchFunction = searchFunctions[this.searchTarget];

        if (searchFunction) {
            return searchFunction(this.searchParam).pipe(switchMap((data: any) => {
                return of({
                    filter,
                    data,
                    skipProcessing: false
                });
            }));
        } else {
            return of({
                filter,
                data: [],
                skipProcessing: false
            });
        }
    }

    private processSearchResults(data: any) {
        if (CommonFunctions.hasArrayValue(data.resultList)) {
            this.data = data.resultList.map((item: any) => new v_QuickSearchBus(item));
        } else {
            this.data = [];
        }
    }

    setLoading(loading: boolean) {
        this.loading = loading;
    }
    /**********************************************************************
     * 0 - Location Name
     * 1 - DispatchNumber
     * 2 - PONumber
     * 3 - WONumber
     * 4 - InvoiceNumber
     * 5 - QuoteNumber
     * 6 - OrderNumber
     * 7 - dispatchId
     **********************************************************************/
    valueChange(value: any, isConstruction: boolean = false) {
        this.data = [];
        if (this.controlType === ControlType.comboBox) {
            this.cb.reset();
        }
        else if (value.length == 36 && value.includes('-')) {
                this.event.clear();
                this.event.searchTarget = this.searchTarget;
                this.event.dispatchId = value;
                this.event.isConstruction = isConstruction;
                this.event.id = value;
                this.ac.toggle(false);
                this.svc_data.nextSearchEvent(this.event);
                this.selected.emit(this.event);
                this.resetSearchValue();
        }
    }
    savedSearchValue: string = '';
    resetSearchValue() {
        this.ac.reset();
        this.ac.value = this.savedSearchValue;
    }

    private openPopoutLink(url: string) {
        this.popout.openOrReuse(url); // reuses the window; loads new URL in it
    }

    customerSelected(value: any) {

    }
    openChanged(e: any) {
        this.isActive = e;
    }
    targetChanged(e: any, target: number) {

        if (e === true) {
            this.searchTarget = target;
            if (CommonFunctions.hasStringValue(this.searchParam.search) && this.searchParam.search.length >= this.minSearchSize) {
                this.data = [];
                this.filterChange(this.searchParam.search);
                this.ac.toggle(true);
            }
            this.changeSearchText();
        }
    }
    ngOnChanges() {
        this.changeSearchText();
    }
    getDispatchTitleName() {
        if (this.featureFlagSet['ui-hide-zuggit-menu-items-extended']) { return 'Sales Orders'; } else {return 'Dispatches' }
    }
    changeSearchText() {
        switch (this.searchTarget) {
            case SearchTarget.dispatch:
                return this.searchText = this.getDispatchTitleName();
            case SearchTarget.quote:
                return this.searchText = 'Quotes';
            case SearchTarget.order:
                return this.searchText = 'Orders';
            case SearchTarget.invoice:
                return this.searchText = 'Invoices';
        }
    }
    advSearch: boolean = false;
    advSearchParam: CommonParam;
    doAdvSearch() {
        switch (this.searchTarget) {
            case SearchTarget.dispatch:
                this.advSearchInputParam.code = "Dispatch";
                this.advSearch = true;
                this.advSearchInputParam.openSearch = true;
                break;
            case SearchTarget.quote:
                this.advSearchInputParam.code = "Quotes";
                this.advSearch = true;
                this.advSearchInputParam.openSearch = true;
                break;
            case SearchTarget.order:
                this.advSearchInputParam.code = "Orders";
                this.advSearch = true;
                this.advSearchInputParam.openSearch = true;
                break;
            case SearchTarget.invoice:
                this.advSearchInputParam.code = "Invoices";
                this.advSearch = true;
                this.advSearchInputParam.openSearch = true;
                break;
        }
    }
    advSearchSelected(dataItem: any) {
        this.advSearch = false;
        this.advSearchInputParam.openSearch = false;
        if (dataItem) {
            let id = null;
            switch (this.searchTarget) {
                case SearchTarget.dispatch:
                    id = dataItem.DispatchId;
                    break;
                case SearchTarget.quote:
                    id = dataItem.QuoteId;
                    break;
                case SearchTarget.order:
                    id = dataItem.OrderId;
                    break;
                case SearchTarget.invoice:
                    id = dataItem.InvoiceId;
                    break;
            }
            this.valueChange(id);
        }
    }
}